import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HeartHandshake, FileDown, Eye, Ban, Search, Download } from "lucide-react";

const STATUS_CLS = {
  paid: "bg-green-50 text-green-700 border-green-200",
  created: "bg-amber-50 text-amber-700 border-amber-200",
  verifying: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-600 border-red-200",
  refunded: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const fmt = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

export default function DonationsAdmin() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState("");
  const [exporting, setExporting] = useState(false);
  const [donor, setDonor] = useState(null);
  const load = () => api.get("/donations").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/donations/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/donations/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `npst-donations-register.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Donations register exported");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const downloadReceipt = async (d) => {
    setDownloading(d.id);
    try {
      const res = await api.get(`/donations/${d.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `receipt_${(d.receipt_no || d.id).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate receipt"); } finally { setDownloading(""); }
  };
  const viewDonor = async (d) => {
    try { const { data } = await api.get(`/donations/${d.id}/donor`); setDonor(data); }
    catch { toast.error("Could not load donor details"); }
  };
  const cancelReceipt = async (d) => {
    const reason = window.prompt("Reason for cancelling this receipt? (recorded in the audit trail)");
    if (!reason) return;
    try { await api.post(`/donations/${d.id}/cancel`, { reason }); toast.success("Receipt cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = (items || []).filter((d) => {
    if (!q.trim()) return true;
    const hay = `${d.receipt_no || ""} ${d.name || ""} ${d.phone || ""} ${d.email || ""} ${d.pan || ""} ${d.payment_id || ""} ${d.order_id || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const Card = ({ label, value, tid }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="text-2xl font-serif text-npst-navy" data-testid={tid}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );

  return (
    <>
      <AdminHeader title="Donations" subtitle={stats ? `${stats.total_count} received · ${fmt(stats.total)} total` : ""} />
      <AdminPage>
        <div className="flex justify-end mb-4">
          <Button onClick={exportCsv} disabled={exporting} variant="outline" data-testid="donations-export-btn" className="gap-2">
            <Download size={15} /> {exporting ? "Exporting…" : "Export Register (CSV)"}
          </Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6" data-testid="donations-summary">
          <Card label="Total Received" value={fmt(stats?.total)} tid="donations-total" />
          <Card label={`This FY (${stats?.fy || ""})`} value={fmt(stats?.fy_total)} tid="donations-fy" />
          <Card label="This Month" value={fmt(stats?.month_total)} tid="donations-month" />
          <Card label="Successful" value={stats?.total_count ?? 0} tid="donations-paid-count" />
          <Card label="Pending" value={stats?.pending ?? 0} tid="donations-pending" />
          <Card label="Refunded" value={stats?.refunded ?? 0} tid="donations-refunded" />
        </div>

        <div className="relative mb-4 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search receipt, name, mobile, email, PAN, payment ID…" className="pl-9" data-testid="donations-search" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {items === null ? <p className="p-6 text-slate-400 text-sm">Loading…</p>
            : filtered.length === 0 ? (
              <div className="p-10 text-center"><HeartHandshake size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No donations found.</p></div>
            ) : filtered.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3 gap-4" data-testid="donation-row">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {d.receipt_no && <span className="font-mono text-xs text-npst-gold">{d.receipt_no}</span>}
                    <span className="font-medium text-npst-ink">{d.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 rounded ${STATUS_CLS[d.status] || STATUS_CLS.created}`} data-testid="donation-status">{d.status}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">{d.phone}{d.method ? ` · ${d.method}` : ""}{d.payment_id ? ` · ${d.payment_id}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="font-serif text-lg text-npst-navy">{fmt(d.amount)}</div>
                    <div className="text-[11px] text-slate-400">{(d.paid_at || d.created_at || "").slice(0, 10)}</div>
                  </div>
                  <button onClick={() => viewDonor(d)} title="View donor details" className="text-slate-400 hover:text-npst-navy" data-testid="view-donor-btn"><Eye size={16} /></button>
                  {d.status === "paid" && <button onClick={() => downloadReceipt(d)} disabled={downloading === d.id} title="Download / Regenerate PDF" className="text-slate-400 hover:text-npst-navy disabled:opacity-40" data-testid="generate-receipt-btn"><FileDown size={16} /></button>}
                  {d.status === "paid" && <button onClick={() => cancelReceipt(d)} title="Cancel receipt" className="text-slate-400 hover:text-red-600" data-testid="cancel-receipt-btn"><Ban size={16} /></button>}
                </div>
              </div>
            ))}
        </div>
      </AdminPage>

      <Dialog open={!!donor} onOpenChange={(o) => !o && setDonor(null)}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="font-serif text-2xl">Donor Details</DialogTitle></DialogHeader>
          {donor && (
            <div className="space-y-2 text-sm" data-testid="donor-details">
              {[["Receipt No.", donor.receipt_no], ["Name", donor.name], ["Mobile", donor.phone], ["Email", donor.email],
                ["PAN", donor.pan], ["Aadhaar", donor.aadhaar_masked], ["Address", donor.address],
                ["Amount", fmt(donor.amount)], ["Method", donor.method], ["Status", donor.status],
                ["Razorpay Order ID", donor.order_id], ["Razorpay Payment ID", donor.payment_id],
                ["Paid At", (donor.paid_at || "").replace("T", " ").slice(0, 16)], ["Message", donor.message]].map(([k, v]) => (
                v ? <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-1.5">
                  <span className="text-slate-500">{k}</span><span className="text-npst-ink font-medium text-right break-all">{v}</span></div> : null
              ))}
              {donor.cancel_reason && <div className="text-red-600 text-xs mt-2">Cancelled: {donor.cancel_reason}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
